/**
 * Gaps Slice
 * 
 * Manages GAP (deficiency) data with caching and pagination support
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Gap } from '../../services/gapService';
import { resetReduxState } from '../actions';

export interface GapsState {
  // All gaps for current annual report
  gaps: Gap[];
  totalCount: number;
  lastFetchedReportId: string | null;
  
  // Pagination state
  currentPage: number;
  pageSize: number;
}

const initialState: GapsState = {
  gaps: [],
  totalCount: 0,
  lastFetchedReportId: null,
  currentPage: 1,
  pageSize: 10,
};

const gapsSlice = createSlice({
  name: 'gaps',
  initialState,
  reducers: {
    // Set gaps data
    setGaps: (
      state,
      action: PayloadAction<{
        data: Gap[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.gaps = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.lastFetchedReportId = action.payload.reportId;
    },

    // Update pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when changing page size
    },

    // Clear all gaps data
    clearGapsData: (state) => {
      state.gaps = [];
      state.totalCount = 0;
      state.lastFetchedReportId = null;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setGaps,
  setCurrentPage,
  setPageSize,
  clearGapsData,
} = gapsSlice.actions;

export default gapsSlice.reducer;
